import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useChat from '../hooks/useChat';
import { useUser } from '../UserContext';

const ChatSection = ({ conversationId }) => {
  const { user } = useUser();
  const { messages, loading, error, sendMessage } = useChat(conversationId);
  const [newMessage, setNewMessage] = useState('');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const chatMessagesRef = useRef(null);

  useEffect(() => {
    console.log('ChatSection: Component mounted/updated');
    console.log('ChatSection: conversationId:', conversationId);
    console.log('ChatSection: user:', user);
  }, [conversationId, user]);

  useEffect(() => {
    console.log('ChatSection: Messages updated:', messages);
  }, [messages]);

  useEffect(() => {
    const chatContainer = chatMessagesRef.current;
    if (chatContainer) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = chatContainer;
        const isScrolledToBottom = scrollHeight - scrollTop === clientHeight;
        setShouldAutoScroll(isScrolledToBottom);
      };

      chatContainer.addEventListener('scroll', handleScroll);
      return () => chatContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    if (shouldAutoScroll && chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages, shouldAutoScroll]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() && conversationId && user) {
      console.log('ChatSection: Sending message:', newMessage);
      try {
        await sendMessage(newMessage, user.uid, user.displayName || 'Anonymous');
        setNewMessage('');
        setShouldAutoScroll(true);
      } catch (error) {
        console.error('ChatSection: Error sending message:', error);
      }
    }
  };

  if (!conversationId) {
    console.log('ChatSection: No conversationId provided');
    return <div className="w-full p-4">No conversation selected</div>;
  }

  if (loading) {
    console.log('ChatSection: Loading messages');
    return (
      <div className="w-full p-4 flex flex-col items-center justify-top h-full">
        <FontAwesomeIcon icon="fa-solid fa-spinner" spin className="mb-2" />
        <span>Loading messages...</span>
      </div>
    );
  }

  if (error) {
    console.error('ChatSection: Error loading messages:', error);
    return (
      <div className="w-full p-4 text-red-500">
        Error loading messages: {error}
      </div>
    );
  }

  console.log('ChatSection: Rendering messages, count:', messages.length);

  return (
    <div className="p-2 sm:p-4 h-full flex flex-col">
      <h2 className="text-xl font-semibold mb-2 sm:mb-4">Chat</h2>
      <div className="flex-grow flex flex-col h-[calc(100vh-300px)] sm:h-[calc(100vh-250px)]">
        <div ref={chatMessagesRef} className="flex-grow overflow-y-auto mb-2 sm:mb-4">
          {messages.length === 0 ? (
            <p className="text-gray-500">No messages yet. Start the conversation!</p>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`mb-2 ${message.senderId === user?.uid ? 'text-right' : 'text-left'}`}>
                <span className={`inline-block p-2 rounded-lg ${message.senderId === user?.uid ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <strong className="text-xs sm:text-sm">{message.senderId === user?.uid ? 'You' : message.senderName || 'Unknown User'}:</strong> <span className="text-sm sm:text-base">{message.content}</span>
                </span>
                <div className="text-xs text-gray-500 mt-1">
                  {message.timestamp ? new Date(message.timestamp.toDate()).toLocaleString() : 'Sending...'}
                </div>
              </div>
            ))
          )}
        </div>
        <form onSubmit={handleSendMessage} className="flex">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-grow p-2 border border-gray-300 rounded-l text-sm sm:text-base"
            placeholder="Type a message..."
          />
          <button type="submit" className="bg-blue-500 text-white px-3 sm:px-4 py-2 rounded-r text-sm sm:text-base">Send</button>
        </form>
      </div>
    </div>
  );
};

export default ChatSection;
